import React from "react";
import { Box, Stack } from "@chakra-ui/react";
import type {
  TradePolicyMetric,
  TradePolicySimulateDelta,
  TradePolicySimulateResponse,
} from "../../../types/tradePolicy";
import {
  METRIC_KEYS,
  formatMetricPercentChangeFromBaseline,
  formatMetricValue,
  formatSignedNumber,
  formatSignedPercentChange,
  metricDeltaNumber,
  normalizeWinRatePercent,
  resolveComparisonLaneColumns,
  resolveRemovedMetricsForSubtitle,
} from "./metricFormatters";
import { SimulatorMetricsTable } from "./SimulatorMetricsTable";
import { cn } from "@/lib/utils";
import {
  cnSimulatorMetricsDelta,
  simulatorMetricsDeltaProps,
  simulatorMetricsDeltaToneClass,
  simulatorMetricsLabelClass,
  simulatorMetricsSummaryHeroClass,
  SIMULATOR_METRICS_GRAY,
} from "./simulatorMetricsTypography";

export { METRIC_KEYS, formatMetricValue, formatMetricPercentChangeFromBaseline } from "./metricFormatters";

export function BaselineFilteredMetrics({
  baseline,
  filtered,
  delta,
  result,
  filteredLoading = false,
}: {
  baseline: TradePolicyMetric | undefined;
  filtered: TradePolicyMetric | undefined;
  /** Prefer `result.delta` when present (Exit Placement trades_changed). */
  delta?: TradePolicySimulateDelta | null;
  /** Full simulate payload — Changed column + removed subtitle from `meta.comparison_lane`. */
  result?: TradePolicySimulateResponse | null;
  borderColor?: string;
  textColor?: string;
  subtleColor?: string;
  filteredLoading?: boolean;
}): React.ReactElement {
  const pnlDelta = filteredLoading
    ? null
    : typeof delta?.total_pnl === "number" && Number.isFinite(delta.total_pnl)
      ? delta.total_pnl
      : metricDeltaNumber(baseline?.total_pnl, filtered?.total_pnl);
  const pnlPercentChange = filteredLoading
    ? null
    : formatMetricPercentChangeFromBaseline(baseline?.total_pnl, filtered?.total_pnl);
  const winRateBaseline = normalizeWinRatePercent(baseline?.win_rate);
  const winRateFiltered = normalizeWinRatePercent(filtered?.win_rate);
  const winRateDelta =
    filteredLoading || winRateBaseline == null || winRateFiltered == null ? null : winRateFiltered - winRateBaseline;
  const laneColumns = resolveComparisonLaneColumns(result ?? { baseline, filtered });
  const changedColumn = laneColumns.find((c) => c.lane === "changed");
  const removedMetrics = resolveRemovedMetricsForSubtitle(result ?? { baseline, filtered });
  const tradesRemoved = filteredLoading
    ? null
    : typeof delta?.trade_count === "number" && Number.isFinite(delta.trade_count)
      ? delta.trade_count
      : typeof removedMetrics?.trade_count === "number" && Number.isFinite(removedMetrics.trade_count)
        ? -Math.abs(removedMetrics.trade_count)
        : metricDeltaNumber(baseline?.trade_count, filtered?.trade_count);
  const tradesChangedFromLane =
    typeof changedColumn?.metrics?.trade_count === "number" &&
    Number.isFinite(changedColumn.metrics.trade_count)
      ? changedColumn.metrics.trade_count
      : null;
  const tradesChanged =
    filteredLoading
      ? null
      : typeof delta?.trades_changed === "number" && Number.isFinite(delta.trades_changed)
        ? delta.trades_changed
        : tradesChangedFromLane;
  const primaryTrades =
    tradesChanged != null
      ? { value: tradesChanged, label: "Trades changed" }
      : { value: tradesRemoved, label: "Trades change" };
  const showHybridRemoved =
    tradesChanged != null &&
    tradesRemoved != null &&
    tradesRemoved !== 0 &&
    tradesRemoved !== tradesChanged;

  return (
    <Stack spacing={4} align="stretch">
      <SimulatorMetricsTable
        baseline={baseline}
        filtered={filtered}
        result={result ?? { baseline, filtered }}
        filteredLoading={filteredLoading}
      />

      {!filteredLoading ? (
        <Box
          data-simulator-metrics-summary=""
          borderWidth="1px"
          borderRadius="6px"
          borderColor="#d3d3d3"
          p={4}
          w="100%"
        >
          <Box display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" columnGap={4} w="100%">
            <Box minW={0} display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <p className={`${simulatorMetricsLabelClass} mb-1 text-xs`}>PnL change</p>
              <p
                {...simulatorMetricsDeltaProps(pnlDelta)}
                className={cnSimulatorMetricsDelta(pnlDelta, simulatorMetricsSummaryHeroClass)}
              >
                {pnlDelta == null ? "—" : formatSignedNumber(pnlDelta, 4)}
              </p>
              <p className={`${simulatorMetricsLabelClass} mt-1 text-xs`}>
                {pnlPercentChange == null ? (
                  "— vs baseline"
                ) : (
                  <>
                    <span
                      {...simulatorMetricsDeltaProps(pnlPercentChange)}
                      className={cn(simulatorMetricsDeltaToneClass(pnlPercentChange), "tabular-nums")}
                    >
                      {formatSignedPercentChange(pnlPercentChange)}
                    </span>
                    <span> vs baseline</span>
                  </>
                )}
              </p>
            </Box>
            <Box minW={0} display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <p className={`${simulatorMetricsLabelClass} mb-1 text-xs`}>Win rate change</p>
              <p
                {...simulatorMetricsDeltaProps(winRateDelta)}
                className={cnSimulatorMetricsDelta(winRateDelta, simulatorMetricsSummaryHeroClass)}
              >
                {winRateDelta == null ? "—" : `${formatSignedNumber(winRateDelta)}%`}
              </p>
              <p className={`${simulatorMetricsLabelClass} mt-1 text-xs`}>
                {baseline?.win_rate == null || filtered?.win_rate == null
                  ? "—"
                  : `${formatMetricValue("win_rate", baseline.win_rate)} to ${formatMetricValue("win_rate", filtered.win_rate)}`}
              </p>
            </Box>
            <Box minW={0} display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <p className={`${simulatorMetricsLabelClass} mb-1 text-xs`}>{primaryTrades.label}</p>
              <p className={cn(simulatorMetricsSummaryHeroClass, SIMULATOR_METRICS_GRAY)}>
                {primaryTrades.value == null ? "—" : formatSignedNumber(primaryTrades.value, 0)}
              </p>
              <p className={`${simulatorMetricsLabelClass} mt-1 text-xs`}>
                {showHybridRemoved
                  ? `${formatSignedNumber(tradesRemoved, 0)} removed`
                  : baseline?.trade_count == null || filtered?.trade_count == null
                    ? "—"
                    : `${formatMetricValue("trade_count", baseline.trade_count)} to ${formatMetricValue("trade_count", filtered.trade_count)} trades`}
              </p>
            </Box>
          </Box>
        </Box>
      ) : null}
    </Stack>
  );
}

export function MetricColumn({
  title,
  metric,
  textColor,
  subtleColor,
  compact = false,
}: {
  title: string;
  metric: TradePolicyMetric | undefined;
  textColor: string;
  subtleColor: string;
  compact?: boolean;
}): React.ReactElement {
  const pad = compact ? 2 : 4;
  const titleMb = compact ? 2 : 3;
  const rowGap = compact ? 1 : 2;
  const rowFs = compact ? "xs" : "sm";
  const titleFs = compact ? "xs" : "sm";
  return (
    <Box borderWidth="1px" borderRadius="md" p={pad}>
      <Box
        as="p"
        fontWeight="semibold"
        color={textColor}
        mb={titleMb}
        fontSize={titleFs}
        textTransform="uppercase"
      >
        {title}
      </Box>
      <Stack align="stretch" spacing={rowGap}>
        {METRIC_KEYS.map(({ key, label }) => {
          const v = metric?.[key];
          return (
            <Box key={key} display="flex" justifyContent="space-between" gap={3} fontSize={rowFs}>
              <Box as="span" color={subtleColor}>
                {label}
              </Box>
              <Box as="span" color={textColor} fontWeight="medium" textAlign="right">
                {formatMetricValue(key as string, v)}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
